import { takeLatest, call, put, select } from 'redux-saga/effects';
import { AnyAction } from 'redux';
import {
  fetchRolesSuccess,
  fetchRolesFailure,
  createOrUpdateRoleSuccess,
  createOrUpdateRoleFailure,
  changeUserRoleFailure,
  deleteRoleSuccess,
  deleteRoleFailure,
  resendMemberInviteSuccess,
  resendMemberInviteFailure,
  deleteMemberInviteSuccess,
  deleteMemberInviteFailure,
} from '../../actions/RecruiterDashboard/roles.actions';
import {
  fetchRoles,
  createorUpdateRole,
  changeUserRole,
  deleteRole,
  resendMemberInvite,
  deleteMemberInvite,
  activateRoleMember,
} from '../../api/RecruiterDashboard/roles.api';
import { IRole } from '../../types/roles.types';
import { IInitialState } from '../../types/initialState.types';
import {
  FETCH_ROLES,
  CREATE_OR_UPADTE_ROLE,
  CHANGE_USER_ROLE,
  DELETE_ROLE,
  DELETE_MEMBER_INVITE,
  RESEND_MEMBER_INVITE,
  ACTIVATE_MEMBER,
} from '../../constants/ActionTypes';
import { showAlert } from '../../actions/alert.action';
import { EAlertType } from '../../types/alert.types';

function* fetchRolesSaga(action: AnyAction): Generator<any, void, IRole[]> {
  try {
    const { username, companySlug } = action.payload;
    const response: IRole[] = yield call(fetchRoles, {
      username,
      companySlug,
    });
    yield put(fetchRolesSuccess(response));
  } catch (error: unknown) {
    const errorMessage =
      error instanceof Error ? error.message : 'Unknown error';
    yield put(fetchRolesFailure(errorMessage));
  }
}

function* createOrUpdateRoleSaga(action: AnyAction): Generator<any, void, any> {
  try {
    const { isEditMode = false, data } = action.payload;
    const response: IRole = yield call(createorUpdateRole, data, isEditMode);
    yield put(createOrUpdateRoleSuccess(response));
    yield put(
      showAlert({
        show: true,
        message: `${
          isEditMode ? ' Role edited' : 'New role added'
        } successfully.`,
        type: EAlertType.SUCCESS,
        version: 'v1',
      })
    );

    // After successful role creation, fetch updated roles list
    const {
      userIdentityData: { username },
    } = yield select((state: IInitialState) => ({
      userIdentityData: state.auth.userIdentityData,
    }));
    const updatedRoles: IRole[] = yield call(fetchRoles, {
      username,
      companySlug: data.company,
    });
    yield put(fetchRolesSuccess(updatedRoles));
  } catch (error: unknown) {
    const errorMessage =
      error instanceof Error ? error.message : 'Unknown error';
    yield put(createOrUpdateRoleFailure(errorMessage));
  }
}

function* changeUserRoleSaga(action: AnyAction): Generator<any, void, any> {
  try {
    const {
      userIdentityData: { username },
      company: { slug },
    } = yield select((state: IInitialState) => ({
      userIdentityData: state.auth.userIdentityData,
      company: state.company,
    }));
    const { currentRole, newRole, email, companySlug } = action.payload;
    const response2 = yield call(
      changeUserRole,
      {
        current_role: currentRole,
        new_role: newRole,
        email,
      },
      username
    );
    yield put(
      showAlert({
        show: true,
        message: 'Role changed successfully!',
        type: EAlertType.SUCCESS,
        version: 'v1',
      })
    );

    // After successful role change, fetch updated roles
    const response: IRole[] = yield call(fetchRoles, {
      username,
      companySlug: companySlug || slug,
    });
    yield put(fetchRolesSuccess(response));
  } catch (error: unknown) {
    const errorMessage =
      error instanceof Error ? error.message : 'Unknown error';
    yield put(changeUserRoleFailure(errorMessage));
  }
}

function* deleteRoleSaga(action: AnyAction): Generator<any, void, any> {
  try {
    const { roleSlug, companySlug } = action.payload;
    yield call(deleteRole, roleSlug);
    yield put(deleteRoleSuccess());
    yield put(
      showAlert({
        show: true,
        message: 'Role deleted successfully.',
        type: EAlertType.SUCCESS,
        version: 'v1',
      })
    );

    // After successful role creation, fetch updated roles list
    const {
      userIdentityData: { username },
      company: { slug },
    } = yield select((state: IInitialState) => ({
      userIdentityData: state.auth.userIdentityData,
      company: state.company,
    }));
    const updatedRoles: IRole[] = yield call(fetchRoles, {
      username,
      companySlug: companySlug || slug,
    });
    yield put(fetchRolesSuccess(updatedRoles));
  } catch (error: unknown) {
    const errorMessage =
      error instanceof Error ? error.message : 'Unknown error';
    yield put(deleteRoleFailure(errorMessage));
  }
}

function* resendMemberInviteSaga(action: AnyAction): Generator<any, void, any> {
  try {
    const {
      userIdentityData: { username },
    } = yield select((state: IInitialState) => ({
      userIdentityData: state.auth.userIdentityData,
    }));
    const { inviteId } = action.payload;
    yield call(resendMemberInvite, inviteId, username);
    yield put(
      showAlert({
        show: true,
        message: 'Invitation resent successfully!',
        type: EAlertType.SUCCESS,
        version: 'v1',
      })
    );
    yield put(resendMemberInviteSuccess());
  } catch (error: unknown) {
    const errorMessage =
      error instanceof Error ? error.message : 'Unknown error';
    yield put(resendMemberInviteFailure(errorMessage));
  }
}

function* activateMemberSaga(action: AnyAction): Generator<any, void, any> {
  try {
    const {
      userIdentityData: { username },
      company: { slug },
    } = yield select((state: IInitialState) => ({
      userIdentityData: state.auth.userIdentityData,
      company: state.company,
    }));
    const { username: selectedUserName } = action.payload;
    yield call(activateRoleMember, selectedUserName, username);
    yield put(
      showAlert({
        show: true,
        message: 'Member made active.',
        type: EAlertType.SUCCESS,
        version: 'v1',
      })
    );
    const updatedRoles: IRole[] = yield call(fetchRoles, {
      username,
      companySlug: slug,
    });
    yield put(fetchRolesSuccess(updatedRoles));
  } catch (error: unknown) {
    const errorMessage =
      error instanceof Error ? error.message : 'Unknown error';
    // yield put(resendMemberInviteFailure(errorMessage));
  }
}

function* deleteMemberInviteSaga(action: AnyAction): Generator<any, void, any> {
  try {
    const {
      userIdentityData: { username },
    } = yield select((state: IInitialState) => ({
      userIdentityData: state.auth.userIdentityData,
    }));
    const { inviteId, companySlug } = action.payload;
    yield call(deleteMemberInvite, inviteId, username);
    yield put(deleteMemberInviteSuccess());
    yield put(
      showAlert({
        show: true,
        message: 'Invite deleted successfully',
        type: EAlertType.SUCCESS,
        version: 'v1',
      })
    );

    const updatedRoles: IRole[] = yield call(fetchRoles, {
      username,
      companySlug,
    });
    yield put(fetchRolesSuccess(updatedRoles));
  } catch (error: unknown) {
    const errorMessage =
      error instanceof Error ? error.message : 'Unknown error';
    yield put(deleteMemberInviteFailure(errorMessage));
  }
}

export default function* loadRolesSaga() {
  yield takeLatest(FETCH_ROLES, fetchRolesSaga);
  yield takeLatest(CHANGE_USER_ROLE, changeUserRoleSaga);
  yield takeLatest(CREATE_OR_UPADTE_ROLE, createOrUpdateRoleSaga);
  yield takeLatest(DELETE_ROLE, deleteRoleSaga);
  yield takeLatest(DELETE_MEMBER_INVITE, deleteMemberInviteSaga);
  yield takeLatest(RESEND_MEMBER_INVITE, resendMemberInviteSaga);
  yield takeLatest(ACTIVATE_MEMBER, activateMemberSaga);
}
